import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { useDeleteStoryboards } from "@/hooks/storyboards/useDeleteStoryboards";
import {
  useDeleteShortcut,
  useLeftPanelShortcuts,
} from "@/hooks/keyboard-shortcuts";
import { useProjectLaunch } from "@/hooks/useProjectLaunch";
import {
  fetchFileTree,
  resetCodebaseState,
  setUnauthorized,
} from "@/store/codebaseSlice";
import { fetchGitStatus, resetGithubState } from "@/store/gitStatusSlice";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { default as useActivityStore } from "@/store/zustand/activityStore";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import {
  RemoveJsxChange,
  UnknownChange,
} from "@/store/zustand/changeLedgerTypes";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  resetStacksToActivityStream,
  useDesignCommandManager,
} from "@/store/zustand/designCommandManagerStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { useEnvVariablesStore } from "@/store/zustand/envVariablesStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { default as usePlanInfoStore } from "@/store/zustand/planInfoStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { useProjectCacheStore } from "@/store/zustand/projectHistoryStore";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { generateJsonStream } from "@/utils/ai/utils";
import { axiosGet } from "@/utils/axiosWrapper";
import { track } from "@/utils/events/tracker";
import { constructFigmaMessage } from "@/utils/figmaUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  fixJson,
  getAxiosErorrMessage,
  getUnexpiredToken,
  useActiveCanvas,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
} from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import type { SignedInSessionResource } from "@clerk/types";
import { default as axios } from "axios";
import _ from "lodash";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useExtractElementStore } from "../Canvas/stores/extractElementStore";
import {
  useDevServerLaunched,
  useProjectLaunchStatus,
} from "@/utils/devServerUtils";

const getGetAndFileTreeFunction = () => {
  return _.debounce(
    (session: SignedInSessionResource, canvasId: any, dispatch: any) => {
      if (!session) {
        return;
      }

      dispatch(
        fetchGitStatus({
          session,
          canvasId,
        }),
      );
      dispatch(
        fetchFileTree({
          session,
          canvasId,
        }),
      );
    },
    500,
    { leading: false, trailing: true },
  );
};

/**
 * These are all side effects/background effects that do not directly affect any renders
 * but apply in all cases when the editor is open
 */
export const EditorSideEffects = React.memo(() => {
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );

  const { session } = useSession();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const canvasId = useCanvasId();

  const dispatch = useDispatch<any>();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const projectLaunchStatus = useProjectLaunchStatus();
  const devServerLaunched = useDevServerLaunched();
  const openFile = useCodebaseStore((state) => state.openFile);
  const closeAllFiles = useCodebaseStore((state) => state.closeAllFiles);

  const iframeChangedKey = useSelector(
    (state: any) => state.iframe.iframeChangedKey,
  );

  // Track the element that need to be highlighted/selected
  const { selectedElement, selectedNavTreeNode, storyboardIdSelected } =
    useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();

  const prevSelectedElement = usePrevious(selectedElement);

  // Plan info
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  // For the left nav
  const nodeOpenDict = useCanvasStore((store) => store.nodeOpenDict);
  const setNodeOpenDict = useCanvasStore((store) => store.setNodeOpenDict);

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup,
  );

  const project = useActiveProject();
  const { fetchEnvVariables } = useEnvVariablesStore();

  const selectedTreeElement = (treeElementLookup || {})[
    selectedElement.codebaseId
  ];

  // Project from DB
  const canvas = useActiveCanvas();
  const mainCanvas = useActiveMainCanvas();

  useEffect(() => {
    const storyboardIdToType = {};
    canvas?.storyboards?.forEach((storyboard) => {
      storyboardIdToType[storyboard.id] = storyboard.type;
    });
    mainCanvas?.storyboards?.forEach((storyboard) => {
      storyboardIdToType[storyboard.id] = storyboard.type;
    });

    if (
      _.isEqual(
        storyboardIdToType,
        useCanvasStore.getState().storyboardIdToType,
      )
    ) {
      return;
    }

    useCanvasStore.getState().setStoryboardIdToTypes(storyboardIdToType);
  }, [canvas?.storyboards, mainCanvas?.storyboards]);

  const filesToReload = useCodebaseStore((state) => state.filesToReload);
  const editorContext = useCodebaseStore((state) => state.editorContext);
  const editorState = useCodebaseStore((state) => state.editor);
  const loadAllNewFiles = useCodebaseStore((state) => state.loadAllNewFiles);

  const triggerDeleteElement = useCanvasStore(
    (state) => state.triggerDeleteElement,
  );
  const setTriggerDeleteElement = useCanvasStore(
    (state) => state.setTriggerDeleteElement,
  );

  // Other hooks
  useProjectLaunch();
  useLeftPanelShortcuts();
  useDeleteShortcut({ onDelete: () => onDeleteRef.current() });

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const { deleteStoryboards, renderConfirmDeleteDialog } =
    useDeleteStoryboards();

  const [searchParams, setSearchParams] = useSearchParams();

  const figmaContextId = searchParams.get("figmaContextId");
  const generatePRDFlow: boolean = searchParams.get("generate-prd") === "t";
  const openDesignMode: boolean = searchParams.get("open-design-mode") === "t";

  const prevGeneratePRDFlow = usePrevious(generatePRDFlow);

  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles,
  );

  const updateFeaturePRD = useIssuesStore((state) => state.updateFeaturePRD);
  const featurePrd = useIssuesStore(
    (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
  );

  const onDeleteElement = useCallback(() => {
    if (selectedElement.isEmpty()) {
      return;
    }

    if (storyboardIdSelected) {
      deleteStoryboards(
        multiselectedElements?.length
          ? multiselectedElements
          : [selectedElement],
      );
      return;
    }

    if (!selectedElement.isKnownElement()) {
      toast.error(
        "Element cannot be found in the codebase, try to select a different element or refresh the frame.",
      );
      return;
    }

    // If the selected element is the top level node of a storyboard, delete the storyboard
    if (selectedNavTreeNode && !selectedNavTreeNode?.parent) {
      deleteStoryboards([
        TempoElement.forStoryboard(
          selectedNavTreeNode.tempoElement.storyboardId,
        ),
      ]);
      return;
    }

    const removeChange = new RemoveJsxChange({
      codebaseIdsToRemove: multiselectedElements?.length
        ? multiselectedElements.map((tempoElement) => tempoElement.codebaseId)
        : [selectedElement.codebaseId],
    });

    // addChange(removeChange);
    useDesignCommandManager.getState().executeCommand(removeChange);

    // Deselect all after instant update
    removeChange.clearSelectedElementsAfterInstantUpdate();

    // After an undo, reselect the deleted element
    removeChange.setSelectedElementsAfterUndoInstantUpdate(
      selectedElement.getKey(),
      multiselectedElements?.map((e) => e.getKey()),
    );
  }, [
    selectedElement,
    multiselectedElements,
    deleteStoryboards,
    storyboardIdSelected,
    selectedNavTreeNode,
  ]);

  const onDeleteRef = useRef(onDeleteElement);
  useEffect(() => {
    onDeleteRef.current = onDeleteElement;
  }, [onDeleteElement]);

  useEffect(() => {
    document.title = project?.name ? `${project?.name} - Tempo` : "Tempo";
  }, [project?.name]);

  useEffect(() => {
    if (triggerDeleteElement) {
      setTriggerDeleteElement(false);
      onDeleteElement();
    }
  }, [triggerDeleteElement]);

  const resetAiActionStore = useAiActionStore(
    (state) => state.resetAiActionStore,
  );
  const resetAiContextStore = useAiContextStore(
    (state) => state.resetAiContextStore,
  );
  const resetCanvasStore = useCanvasStore((state) => state.resetCanvasStore);
  const resetDevServerStore = useDevServerStore(
    (state) => state.resetDevServerStore,
  );
  const resetCodebaseStore = useCodebaseStore(
    (state) => state.resetCodebaseStore,
  );
  const resetLayoutStore = useLayoutStore((state) => state.resetLayoutStore);
  const resetSupabaseConnectStore = useSupabaseConnectStore(
    (state) => state.resetSupabaseConnectStore,
  );
  const resetDesignCommandManagerStore = useDesignCommandManager(
    (state) => state.resetDesignCommandManagerStore,
  );
  const setSession = useDesignCommandManager((state) => state.setSession);

  /**
   * When the activity stream changes, if the activity doesn't exist in the undo/redo stack
   * add it in
   */
  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList,
  );

  useEffect(() => {
    if (!activityStreamList?.length) {
      return;
    }

    const {
      undoStack,
      redoStack,
      setUndoStack,
      serverChangeQueue,
      deletedActivityIds,
    } = useDesignCommandManager.getState();

    // If the undo & redo stack are empty, add all the activities to it
    if (
      undoStack.length === 0 &&
      redoStack.length === 0 &&
      activityStreamList.length
    ) {
      resetStacksToActivityStream();
      return;
    }

    // Check if the newest activity might be missing from the undo stack
    const newestActivity = activityStreamList[0];

    if (
      !undoStack.find((change) => change.activityId === newestActivity.id) &&
      !redoStack.find((change) => change.activityId === newestActivity.id) &&
      !deletedActivityIds.has(newestActivity.id)
    ) {
      const newActivity = new UnknownChange({});
      newActivity.activityId = newestActivity.id;

      // Add to the END of the undo stack so it gets undone first (since undo() pops from the end)
      setUndoStack([...undoStack, newActivity]);
      return;
    }

    // Otherwise, check if the activity stream matches the undo/redo stack assuming there are no queued up server changes
    if (serverChangeQueue?.length) {
      return;
    }

    // 1) Are all activities in the activity stream either in the undo or redo stack?
    // 2) If an activity is undone, it should be in the redo stack
    // 3) If an activity is redone, it should be in the undo stack
    const valid = activityStreamList.every((activity) => {
      if (activity.change_undone) {
        // Activity should be in the redo stack
        return redoStack.find((change) => change.activityId === activity.id);
      } else {
        // Activity should be in the undo stack
        return undoStack.find((change) => change.activityId === activity.id);
      }
    });

    if (!valid) {
      resetStacksToActivityStream();
    }
  }, [activityStreamList]);

  const canvasProjectId = mainCanvas?.project_id;
  useEffect(() => {
    if (canvasProjectId) {
      useProjectCacheStore.getState().recordProjectAccess(canvasProjectId);
    }
  }, [canvasProjectId]);

  /**
   * When the user opens a new editor, reset the editor state
   */
  useEffect(() => {
    if (canvasId) {
      resetLayoutStore();
      dispatch(resetCodebaseState());
      dispatch(resetGithubState());
      resetAiActionStore();
      resetAiContextStore();
      resetCanvasStore();
      resetCodebaseStore();
      resetSupabaseConnectStore();
      resetDevServerStore();
      resetDesignCommandManagerStore(canvasId);
      useExtractElementStore.getState().resetStore();
    }
  }, [canvasId]);

  useEffect(() => {
    if (!session) {
      return;
    }

    setSession(session);
  }, [session]);

  useEffect(() => {
    if (!canvasId || !mainCanvas?.project_id) {
      return;
    }

    track("PROJECT_OPEN");
  }, [canvasId, mainCanvas?.project_id]);

  const hasSession = Boolean(session);

  /**
   * Set files in the editor whenever the selectedElement or css selector changes
   */
  useEffect(() => {
    if (!prevSelectedElement || selectedElement.isEqual(prevSelectedElement)) {
      return;
    }

    if (storyboardIdSelected) {
      const selectedStoryboard = canvas?.storyboards?.find(
        (storyboard) => storyboard.id === storyboardIdSelected,
      );

      if (selectedStoryboard?.fullFilePath) {
        openFile({
          file: {
            filename: selectedStoryboard?.fullFilePath,
            hardOpened: false,
            context: EditorContext.Code,
          },
          jumpToTab: true,
        });
      }
      return;
    }

    if (selectedTreeElement) {
      openFile({
        file: {
          filename: selectedTreeElement?.filename,
          highlightLines: {
            charPos: selectedTreeElement?.start,
          },
          hardOpened: false,
          context: EditorContext.Code,
        },
        jumpToTab: true,
      });
      return;
    }
  }, [selectedElement, storyboardIdSelected]);

  useEffect(() => {
    if (canvasId) {
      closeAllFiles();
    }
  }, [canvasId]);

  const fetchGitAndFileTree = useRef(getGetAndFileTreeFunction());

  useEffect(() => {
    if (!session) {
      return;
    }

    fetchGitAndFileTree.current(session, canvasId, dispatch);
  }, [
    projectLaunchStatus,
    devServerLaunched,
    iframeChangedKey,
    canvasId,
    Boolean(session),
  ]);

  /**
   * Open nav tree to given node when element is selected
   */
  useEffect(() => {
    // This check can be true even if codebase ID is not set
    if (!selectedElement.isEmpty()) {
      let navNodeToOpen =
        elementKeyToNavNodeLookup[selectedElement.getKey()]?.parent;
      while (navNodeToOpen) {
        nodeOpenDict[navNodeToOpen.tempoElement.getKey()] = true;
        navNodeToOpen = navNodeToOpen.parent;
      }
      setNodeOpenDict({ ...nodeOpenDict });
    }
  }, [selectedElement]);

  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    const generateTitles = async () => {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiGenerateTitleAndProjectName`;

      const supabaseJWT = await getUnexpiredToken(session);

      const body = {
        visual_context:
          generateAttachedFiles && generateAttachedFiles.length > 0
            ? generateAttachedFiles.map((file) => ({
                imageUrl: file.url,
                context: "Image related to the app",
              }))
            : undefined,
        generateText,
      };

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      let res = null;

      for await (const chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          // No straming
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      // @ts-expect-error TS18047
      const finalResult = res.data.steps[0].text;
      const parsedResult = fixJson(finalResult).fixedObj;

      updateFeaturePRD(supabaseClient, {
        id: featurePrd?.id,
        canvas_id: canvasId,
        title: parsedResult.feature_title,
      });

      const updatedProject = {
        ...project,
        name: parsedResult.project_name,
      };

      dispatch(
        onProjectsSnapshot({
          newItems: [updatedProject as any],
          deletedItems: [],
        }),
      );

      await supabaseClient
        .from("projects")
        .update({ name: parsedResult.project_name })
        .eq("id", project?.id);
    };

    if (
      project &&
      generateText &&
      prevGeneratePRDFlow &&
      !generatePRDFlow &&
      featurePrd?.prd
    ) {
      try {
        generateTitles();
      } catch (e) {
        console.error(e);
      } finally {
        refreshPlanInfo(session);
      }
    }
  }, [featurePrd, project, generatePRDFlow, supabaseClient]);

  /**
   * When files are changed, load them from the backend
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    loadAllNewFiles(canvasId, session);
  }, [filesToReload, Boolean(session), editorContext, editorState]);

  const authorized = useSelector((state: any) => state.codebase.authorized);
  const checkingAuthorized = useRef(false);

  /**
   * Check if the user is authorized for this canvas
   */
  useEffect(() => {
    if (
      (authorized !== null && authorized !== undefined) ||
      checkingAuthorized.current
    ) {
      // Already checked
      return;
    }

    if (!session) {
      return;
    }

    checkingAuthorized.current = true;

    const checkAuthorized = async () => {
      const supabaseJWT = await getUnexpiredToken(session);
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/hasAccess`;
      axiosGet(url, {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
        showToast: false,
        throwOnError: false,
      }).then((response) => {
        if (response?.data?.authorized === false) {
          dispatch(setUnauthorized(true));
        } else {
          dispatch(setUnauthorized(false));
        }
      });
    };

    checkAuthorized();
  }, [authorized, session, canvasId, dispatch]);

  const setUsersForProject = useCanvasStore(
    (state) => state.setUsersForProject,
  );

  /**
   * Fetch users for the project
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    async function fetchUsers() {
      const supabaseJWT = await getUnexpiredToken(session);
      const axiosInstance = axios.create({
        baseURL: import.meta.env.VITE_BACKEND_BASE_URL,
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      });

      const usersResp = await axiosInstance.get(
        `/projects/${mainCanvas?.project_id}/users`,
      );

      setUsersForProject(
        mainCanvas?.project_id,
        usersResp?.data.users.map((user: any) => ({
          name: user.name,
          email: user.email,
          id: user.auth0_user_id,
          picture: user.picture,
          is_selected: false,
        })) || [],
      );
    }

    if (mainCanvas?.project_id) {
      fetchUsers();
    }
  }, [mainCanvas?.project_id, Boolean(session)]);

  /**
   * Fetch environment variables when project is opened
   */
  useEffect(() => {
    if (session && project?.id) {
      fetchEnvVariables(project.id, session);
    }
  }, [Boolean(session), project?.id, fetchEnvVariables]);

  useEffect(() => {
    if (openDesignMode) {
      // @ts-expect-error TS2345
      setNewLayout(null, LayoutMode.Design);
    }
  }, [openDesignMode, setNewLayout]);

  /**
   * Fetch figma project id if existing
   */
  useEffect(() => {
    if (!figmaContextId || !session || projectLaunchStatus !== "succeeded")
      return;

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("figmaContextId");
      return newParams;
    });
    // @ts-expect-error TS2345
    setNewLayout(null, LayoutMode.Design);

    track("FIGMA_IMPORT", {
      figmaContextId,
    });

    getUnexpiredToken(session).then((supabaseJWT: string) => {
      axios
        .get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/figma/context/${figmaContextId}`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        )
        .then((response) => {
          if (response.status === 404) {
            toast.error(
              "Figma context not found, may have already been fetched and deleted.",
            );
            console.error(
              "Figma context not found, may have already been fetched and deleted.",
            );
            return;
          }
          if (response.status !== 200) {
            toast.error(`Error fetching Figma data: ${response}`);
            console.error(`Error fetching Figma data: ${response}`);
            return;
          }
          const { initial_code, image_url, figma_context, frame_images } =
            response.data;

          let figmaImages = "";
          let figmaIcons = "";
          try {
            const frameImagesJson = JSON.parse(frame_images);

            for (const image of frameImagesJson.images) {
              figmaImages += `\n<image_${image.nodeId}>\n${image.url}\n</image_${image.nodeId}>\n`;
            }

            for (const icon of frameImagesJson.icons) {
              figmaIcons += `\n<icon_${icon.nodeId}>\n${icon.svg}\n</icon_${icon.nodeId}>\n`;
            }
          } catch (e) {
            console.error(e);
            toast.error("Error parsing Figma data, please try again.");
            return;
          }

          const figmaMessage = constructFigmaMessage(
            initial_code,
            figma_context,
            figmaImages,
            figmaIcons,
          );

          setSendChatMessage({
            sendChatMessage: `\n<figma_input>\n${figmaMessage}\n</figma_input>\n`,
            sendChatMessageFiles: [
              {
                url: image_url,
                name: "figma_image",
                type: "image",
                mimeType: "image/png",
              },
            ],
            resetToggles: "all",
          });
        })
        .catch((error) => {
          toast.error(
            `Error fetching Figma data: ${getAxiosErorrMessage(error)}`,
          );
          console.error(
            "Error fetching Figma data:",
            getAxiosErorrMessage(error),
          );
        });
    });
  }, [figmaContextId, Boolean(session), projectLaunchStatus]);

  return <>{renderConfirmDeleteDialog()}</>;
});
